import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, DataTable, Field, LoadingState, TextInput } from "@odingard/design-system";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

interface Member {
  id: string;
  displayName: string;
  email: string;
  role: string;
}
const ROLE_OPTIONS = ["owner", "admin", "member", "viewer"];
const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
}

export function Users() {
  const { user, activeOrg, loading } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState(true);

  const canManage = Boolean(activeOrg?.permissions?.includes("org:manage"));

  async function changeRole(member: Member, next: string) {
    if (!activeOrg || next === member.role) return;
    setError(null);
    setNotice(null);
    setMembers((current) => current.map((m) => (m.id === member.id ? { ...m, role: next } : m)));
    try {
      await api.setMemberRole(activeOrg.id, member.id, next);
      setNotice(`${member.displayName || member.email} is now ${ROLE_LABELS[next] ?? next}.`);
    } catch (err) {
      setMembers((current) => current.map((m) => (m.id === member.id ? { ...m, role: member.role } : m)));
      setError(err instanceof Error ? err.message : "Could not change that role.");
    }
  }

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setDataLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.members(activeOrg.id),
        canManage ? api.invitations(activeOrg.id) : Promise.resolve({ invitations: [] }),
      ]);
      setMembers(m.members);
      setInvites(i.invitations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load members.");
    } finally {
      setDataLoading(false);
    }
  }, [activeOrg, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!activeOrg) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.createInvitation(activeOrg.id, { email, role });
      setNotice(res.sent ? `Invitation sent to ${res.email}.` : `Invitation created. Share this link: ${res.link}`);
      setEmail("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the invitation.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!activeOrg) return;
    try {
      await api.revokeInvitation(activeOrg.id, id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke the invitation.");
    }
  }

  if (loading) return <LoadingState />;
  if (!user || !activeOrg) return null;

  const memberCols = [
    { key: "name", header: "Name", render: (r: Member) => r.displayName },
    { key: "email", header: "Email", render: (r: Member) => r.email },
    {
      key: "role",
      header: "Role",
      render: (r: Member) =>
        canManage && r.id !== user?.id ? (
          <select
            className="ods-select"
            value={r.role}
            aria-label={`Role for ${r.displayName || r.email}`}
            onChange={(e) => void changeRole(r, e.target.value)}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABELS[option]}
              </option>
            ))}
          </select>
        ) : (
          <Badge tone="info">{ROLE_LABELS[r.role] ?? r.role}</Badge>
        ),
    },
  ];
  const inviteCols = [
    { key: "email", header: "Email", render: (r: Invite) => r.email },
    { key: "role", header: "Role", render: (r: Invite) => <Badge tone="neutral">{r.role}</Badge> },
    { key: "status", header: "Status", render: (r: Invite) => <Badge tone="warning">{r.status}</Badge> },
    {
      key: "actions",
      header: "",
      render: (r: Invite) => (
        <Button variant="ghost" onClick={() => void revoke(r.id)}>
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p>People with access to {activeOrg.name}.</p>
        </div>
      </div>

      {error && (
        <div className="banner banner--warning" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="banner banner--info" role="status">
          {notice}
        </div>
      )}

      {dataLoading ? (
        <LoadingState />
      ) : (
        <>
          <DataTable columns={memberCols} rows={members} caption="Organization members" />
          <p className="muted" style={{ marginTop: "var(--ods-space-3)" }}>
            Role decides what someone may do in Governance and Compliance. Owners and admins
            approve evidence, decide exceptions, accept residual risk and export reports. Members
            do the work: frameworks, evidence, exceptions, regulations and audit projects. Viewers
            read.
          </p>
        </>
      )}

      {canManage && (
        <div style={{ marginTop: "var(--ods-space-6)" }}>
        <Card>
          <CardBody>
            <h2 style={{ fontSize: "var(--ods-text-lg)", marginTop: 0 }}>Invite a teammate</h2>
            <form onSubmit={invite} style={{ display: "flex", gap: "var(--ods-space-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 240px" }}>
                <Field label="Work email">
                  {(id) => (
                    <TextInput id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  )}
                </Field>
              </div>
              <div>
                <Field label="Role">
                  {(id) => (
                    <select
                      id={id}
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="ods-select"
                      style={{ height: 40 }}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  )}
                </Field>
              </div>
              <Button type="submit" disabled={busy}>
                {busy ? "Sending…" : "Send invite"}
              </Button>
            </form>
            {invites.length > 0 && (
              <div style={{ marginTop: "var(--ods-space-5)" }}>
                <DataTable columns={inviteCols} rows={invites} caption="Pending invitations" />
              </div>
            )}
          </CardBody>
        </Card>
        </div>
      )}
    </>
  );
}
